import * as React from 'react'

import type {
  TableFieldDef,
  TableFields,
} from '~/client/src/desktop/tables/tableFields'
import { buildTableFields } from '~/client/src/desktop/tables/tableFields'
import TagListCell from '~/client/src/desktop/tables/TagListCell'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import type { ListQuery } from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import {
  CompanyClassification,
  CompanyDirectoryColumnId,
  CompanyStatus,
  COMPANY_TYPE_GROUP_KEY,
  ICompanyRow,
} from './types'

export const COMPANY_INITIAL_QUERY: Partial<ListQuery> = {
  appliedFacets: {
    [CompanyDirectoryColumnId.Status]: [CompanyStatus.Active],
  },
  sortKey: CompanyDirectoryColumnId.Name,
  sortOrder: SortOrder.ASC,
}

export function getCompanyStatusLabel(status: CompanyStatus): string {
  const t = Localization.translator
  switch (status) {
    case CompanyStatus.Active:
      return t.active
    case CompanyStatus.Deactivated:
      return t.deactivated
  }
}

export function getCompanyClassificationLabel(
  classification: CompanyClassification,
): string {
  const t = Localization.translator
  switch (classification) {
    case CompanyClassification.Common:
      return t.common
    case CompanyClassification.VendorOnly:
      return `${t.vendor} ${t.only}`
  }
}

function joinOrNoValue(values: readonly string[]): string {
  return values.length ? values.join(', ') : NO_VALUE
}

function CompanyNameCell({
  row,
}: {
  readonly row: ICompanyRow
}): React.ReactElement {
  return (
    <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
      <Avatar
        src={row.avatarUrl || undefined}
        avatarType={AvatarType.Company}
        size={AvatarSize.Minuscule}
      />
      <span className="tw:truncate">{row.name || NO_VALUE}</span>
    </div>
  )
}

function ResponsibleUsersCell({
  row,
}: {
  readonly row: ICompanyRow
}): React.ReactElement {
  if (!row.responsibleUsers.length) return <>{NO_VALUE}</>

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1 tw:py-1">
      {row.responsibleUsers.map(user => (
        <div key={user.id} className="tw:min-w-0 tw:overflow-hidden">
          <UserProfilePreview user={user} />
        </div>
      ))}
    </div>
  )
}

export function buildCompanyFields(): TableFields<ICompanyRow> {
  const t = Localization.translator
  const fields: readonly TableFieldDef<ICompanyRow>[] = [
    {
      key: CompanyDirectoryColumnId.Name,
      label: t.companyName,
      value: row => row.name,
      sortable: true,
      fixed: true,
      column: {
        size: 240,
        cell: record => <CompanyNameCell row={record} />,
      },
    },
    {
      key: CompanyDirectoryColumnId.Code,
      label: t.code,
      value: row => row.code,
      sortable: true,
      column: {
        size: 130,
        cell: record => record.code || NO_VALUE,
      },
    },
    {
      key: CompanyDirectoryColumnId.WhitelistedDomains,
      label: t.whitelistedDomains,
      value: row => row.whitelistedDomains,
      searchable: false,
      column: {
        size: 200,
        enableSorting: false,
        cell: record => joinOrNoValue(record.whitelistedDomains),
      },
    },
    {
      key: CompanyDirectoryColumnId.Roles,
      label: t.projectRoles,
      value: row => row.roleTags.map(tag => tag.name),
      searchable: false,
      column: {
        size: 200,
        enableSorting: false,
        cell: record => <TagListCell tags={record.roleTags} />,
      },
    },
    {
      key: CompanyDirectoryColumnId.Trades,
      label: t.projectTrades,
      value: row => row.tradeTags.map(tag => tag.name),
      searchable: false,
      column: {
        size: 200,
        enableSorting: false,
        cell: record => <TagListCell tags={record.tradeTags} />,
      },
    },
    {
      key: CompanyDirectoryColumnId.CompanyType,
      label: t.companyType,
      value: row => row.typeTags.map(tag => tag.id),
      labelValue: row => row.typeTags.map(tag => tag.name),
      facetable: true,
      searchable: false,
      column: {
        size: 200,
        enableSorting: false,
        cell: record => <TagListCell tags={record.typeTags} />,
      },
    },
    {
      key: COMPANY_TYPE_GROUP_KEY,
      label: t.companyType,
      value: row => row.typeTags.map(tag => tag.id).join(','),
      labelValue: row => row.typeTags.map(tag => tag.name).join(', '),
      groupable: true,
      searchable: false,
    },
    {
      key: CompanyDirectoryColumnId.ResponsibleUsers,
      label: t.responsibleUser,
      column: {
        size: 240,
        enableSorting: false,
        cell: record => <ResponsibleUsersCell row={record} />,
      },
    },
    {
      key: CompanyDirectoryColumnId.Members,
      label: t.members,
      value: row => row.membersCount,
      sortable: true,
      searchable: false,
      column: {
        size: 110,
        cell: record => record.membersCount,
      },
    },
    {
      key: CompanyDirectoryColumnId.Status,
      label: t.status,
      value: row => row.status,
      labelValue: row => getCompanyStatusLabel(row.status),
      facetable: true,
      groupable: true,
      searchable: false,
      defaultHidden: true,
      column: {
        size: 140,
        enableSorting: false,
        cell: record => getCompanyStatusLabel(record.status),
      },
    },
    {
      key: CompanyDirectoryColumnId.Classification,
      label: t.classification,
      value: row => row.classification,
      labelValue: row => getCompanyClassificationLabel(row.classification),
      facetable: true,
      groupable: true,
      searchable: false,
      defaultHidden: true,
      column: {
        size: 160,
        enableSorting: false,
        cell: record => getCompanyClassificationLabel(record.classification),
      },
    },
  ]

  return buildTableFields({
    getId: row => row.id,
    fields,
  })
}
